//store/useShoppingCartAnonSessionStore.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState, AppDispatch } from "./index";
import { ShoppingCartAnonSessionRequest } from "../domain/ShoppingCartSlice/ShoppingCartAnonSessionRequest";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectIsLoggedInClient } from "./useSecurityStore";
import dayjs from "dayjs";
import { DATE_TIME_FORMAT_ISO } from "../constants";

const ANON_SESSION_EXPIRATION_MINUTES = 240; // 4 hours

export interface ShoppingCartAnonSessionState
  extends ShoppingCartAnonSessionRequest {
  expiresAt: string | null; // ISO timestamp
}

const initialState: ShoppingCartAnonSessionState = {
  sessionId: "",
  shoppingCartId: 0,
  expiresAt: null,
};

function minutesFromNowISO(m: number) {
  return dayjs(new Date(Date.now() + m * 60_000)).format(DATE_TIME_FORMAT_ISO);
}
function isExpired(iso: string | null) {
  if (!iso) return true;
  return new Date(iso).getTime() <= Date.now();
}

const anonSessionSlice = createSlice({
  name: "anonSession",
  initialState,
  reducers: {
    setAnonSession(
      state,
      a: PayloadAction<{ sessionId: string; shoppingCartId: number }>
    ) {
      state.sessionId = a.payload.sessionId;
      state.shoppingCartId = a.payload.shoppingCartId;
      state.expiresAt = minutesFromNowISO(ANON_SESSION_EXPIRATION_MINUTES);
    },
    clearAnonSession(state) {
      console.debug("[anonSession] clearAnonSession()");
      state.sessionId = "";
      state.shoppingCartId = 0;
      state.expiresAt = null;
    },
    // Internal helper to drop expired data on access
    _expireIfNeeded(state) {
      if (isExpired(state.expiresAt)) {
        state.sessionId = "";
        state.shoppingCartId = 0;
        state.expiresAt = null;
      }
    },
  },
});

export const { setAnonSession, clearAnonSession, _expireIfNeeded } =
  anonSessionSlice.actions;

export default anonSessionSlice.reducer;

// Selectors
export const selectRawAnonSession = (s: RootState) => s.shoppingCartAnonSession;

export const selectAnonSession = (s: RootState) => {
  const st = s.shoppingCartAnonSession;
  if (!st || isExpired(st.expiresAt) || !st.sessionId || !st.shoppingCartId) {
    return null; // IMPORTANT: return null instead of a truthy object with nulls
  }
  return {
    sessionId: st.sessionId,
    shoppingCartId: st.shoppingCartId,
  } as const;
};

export const selectHasAnonSession = (s: RootState) => !!selectAnonSession(s);

// Facade hook (so callers can do: const { selectAnonSession } = useShoppingCartAnonSessionStore())
export function useShoppingCartAnonSessionStore() {
  const dispatch = useDispatch<AppDispatch>();
  const selected = useSelector(selectAnonSession);

  const setAnonSessionAction = useCallback(
    (p: { sessionId: string; shoppingCartId: number }) =>
      dispatch(setAnonSession(p)),
    [dispatch]
  );
  const clearAnonSessionAction = useCallback(() => {
    console.debug("[anonSession] dispatch clearAnonSession()");
    return dispatch(clearAnonSession());
  }, [dispatch]);
  const pruneIfExpiredAction = useCallback(
    () => dispatch(_expireIfNeeded()),
    [dispatch]
  );

  //session state
  const isLoggedInClient = useSelector(selectIsLoggedInClient);

  // Clear anon session when auth state flips (login/logout/rehydrate)
  useEffect(() => {
    // Always refresh client data when auth state changes (visibility depends on auth)
    if (isLoggedInClient) {clearAnonSessionAction();}
  }, [dispatch, isLoggedInClient]);

  return {
    anonSession: selected,
    setAnonSession: setAnonSessionAction,
    clearAnonSession: clearAnonSessionAction,
    pruneIfExpired: pruneIfExpiredAction,
  } as const;
}
